import useUrlState from "@ahooksjs/use-url-state";
import { useGet } from "@albertine/api-client";
import {
    type CurrencyCode,
    CurrencyCodeSchema,
    type ListAllPaymentsData,
    type ListAllPaymentsSortField,
    type PaymentMethod,
    PaymentMethodSchema,
    type PaymentStatus,
    PaymentStatusSchema,
} from "@albertine/types/openapi";
import { useState } from "react";
import { useClaims } from "../../utils/claims.util";
import { formatTotal } from "../../utils/format.util";
import Dropdown from "../Common/Dropdown";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import { List } from "../Common/List";
import ListFilter, { type ListFilterItem } from "../Common/ListFilter";
import Tag from "../Common/Tag";
import { PaymentsPaginatedResults } from "./PaymentsPaginatedResults";
import {
    formatPaymentMethodLabel,
    formatPaymentStatusLabel,
    type PaymentsListFilter,
    type SortOrder,
} from "./paymentsList.util";

type PaymentsUrlQuery = Partial<{
    sortBy: ListAllPaymentsSortField;
    sortOrder: SortOrder;
    currency: string | string[];
    paymentMethod: string | string[];
    createdBy: string | string[];
    status: string | string[];
}>;

const SORT_ENTRIES: [ListAllPaymentsSortField, string][] = [
    ["createdAt", "Sort by created date"],
    ["amount", "Sort by amount"],
    ["currency", "Sort by currency"],
    ["paymentMethod", "Sort by payment method"],
    ["createdBy", "Sort by created by"],
];

const defaultSortBy: ListAllPaymentsSortField = "createdAt";
const defaultSortOrder: SortOrder = "desc";

export function PaymentsListView() {
    const { isAdmin } = useClaims();
    const { data: allAgents } = useGet("getAllAgents", {}, { enabled: isAdmin });
    const [urlQuery, setUrlQuery] = useUrlState<PaymentsUrlQuery>(undefined, {
        parseOptions: { arrayFormat: "index" },
        stringifyOptions: { arrayFormat: "index" },
    });

    const sortBy = urlQuery.sortBy ?? defaultSortBy;
    const sortOrder = urlQuery.sortOrder ?? defaultSortOrder;
    const selectedCurrencies = asCurrencyArray(urlQuery.currency);
    const selectedPaymentMethods = asPaymentMethodArray(urlQuery.paymentMethod);
    const selectedCreatedByIds = asStringArray(urlQuery.createdBy);
    const selectedStatuses = asPaymentStatusArray(urlQuery.status);

    const [total, setTotal] = useState(0);

    const queryFilter: PaymentsListFilter = {
        sortBy,
        sortOrder,
        currency: selectedCurrencies,
        paymentMethod: selectedPaymentMethods,
        createdBy: selectedCreatedByIds,
        status: selectedStatuses,
    };

    function handlePageLoaded(data: ListAllPaymentsData) {
        setTotal(data.total);
    }

    const currencyFilterOptions: ListFilterItem<{ id: CurrencyCode; name: string }>[] =
        CurrencyCodeSchema.options.map((code) => ({
            data: { id: code, name: code },
            selected: selectedCurrencies.includes(code),
        }));

    const paymentMethodFilterOptions: ListFilterItem<{
        id: PaymentMethod;
        name: string;
    }>[] = PaymentMethodSchema.options.map((method: PaymentMethod) => ({
        data: { id: method, name: formatPaymentMethodLabel(method) },
        selected: selectedPaymentMethods.includes(method),
    }));

    const createdByFilterOptions: ListFilterItem<{
        id: string;
        name: string;
    }>[] = (allAgents?.results ?? []).map((agent: { id: string; name: string }) => ({
        data: {
            id: agent.id,
            name: agent.name,
        },
        selected: selectedCreatedByIds.includes(agent.id),
    }));

    const statusFilterOptions: ListFilterItem<{
        id: PaymentStatus;
        name: string;
    }>[] = PaymentStatusSchema.options.map((status) => ({
        data: { id: status, name: formatPaymentStatusLabel(status) },
        selected: selectedStatuses.includes(status),
    }));

    function handleSortByChange(newSortBy: ListAllPaymentsSortField | null) {
        if (newSortBy !== null) {
            setUrlQuery((prev) => ({
                ...prev,
                sortBy: newSortBy,
                sortOrder,
            }));
        }
    }

    function toggleSortOrder() {
        setUrlQuery((prev) => ({
            ...prev,
            sortBy,
            sortOrder: sortOrder === "asc" ? "desc" : "asc",
        }));
    }

    return (
        <List.Tab>
            <List.TabHeader>
                <div className="flex w-full items-center justify-between">
                    <div className="flex flex-row items-center gap-2">
                        <h3>Payments</h3>
                        <Tag.Medium.N90>{formatTotal(total)}</Tag.Medium.N90>
                    </div>
                </div>
            </List.TabHeader>

            <List.Header>
                <List.HeaderColumn className="w-[28%] min-w-0 shrink self-center pr-2 max-md:w-full">
                    <div className="sort-by flex cursor-pointer flex-row items-center gap-2">
                        <button
                            type="button"
                            onClick={toggleSortOrder}
                            className="flex items-center justify-center border-0 bg-transparent p-0"
                        >
                            {sortOrder === "asc" ? (
                                <Icon.ArrowDownward.Medium.N50 />
                            ) : (
                                <Icon.ArrowUpward.Medium.N50 />
                            )}
                        </button>
                        <Dropdown
                            entries={SORT_ENTRIES}
                            currentValue={sortBy}
                            onChange={(value) =>
                                handleSortByChange(
                                    value as ListAllPaymentsSortField | null,
                                )
                            }
                        />
                    </div>
                </List.HeaderColumn>
                <List.HeaderColumn className="w-[10%] min-w-0 shrink self-center pr-2">
                    <div className="flex flex-row items-center gap-1">
                        <Label.Medium.Normal>Amount</Label.Medium.Normal>
                    </div>
                </List.HeaderColumn>
                <List.HeaderColumn className="w-[10%] min-w-0 shrink self-center pr-2 max-md:hidden">
                    <ListFilter<{ id: CurrencyCode; name: string }>
                        title="Currency"
                        items={currencyFilterOptions}
                        onChange={(selected) => {
                            setUrlQuery((prev) => ({
                                ...prev,
                                currency:
                                    selected.length > 0
                                        ? selected.map((it) => it.id)
                                        : undefined,
                            }));
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
                <List.HeaderColumn className="w-[14%] min-w-0 shrink self-center pr-2 max-md:hidden">
                    <ListFilter<{ id: string; name: string }>
                        title="Method"
                        items={paymentMethodFilterOptions}
                        onChange={(selected) => {
                            setUrlQuery((prev) => ({
                                ...prev,
                                paymentMethod:
                                    selected.length > 0
                                        ? selected.map((it) => it.id)
                                        : undefined,
                            }));
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
                <List.HeaderColumn className="w-[18%] min-w-0 shrink self-center pr-2 max-md:hidden">
                    <ListFilter<{ id: string; name: string }>
                        title="Created by"
                        items={createdByFilterOptions}
                        onChange={(selected) => {
                            setUrlQuery((prev) => ({
                                ...prev,
                                createdBy:
                                    selected.length > 0
                                        ? selected.map((it) => it.id)
                                        : undefined,
                            }));
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
                <List.HeaderColumn className="w-[20%] min-w-0 shrink self-center max-md:hidden">
                    <ListFilter<{ id: PaymentStatus; name: string }>
                        title="Status"
                        items={statusFilterOptions}
                        onChange={(selected) => {
                            setUrlQuery((prev) => ({
                                ...prev,
                                status:
                                    selected.length > 0
                                        ? selected.map((it) => it.id)
                                        : undefined,
                            }));
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
            </List.Header>

            <PaymentsPaginatedResults
                filter={queryFilter}
                onPageLoaded={handlePageLoaded}
            />
        </List.Tab>
    );
}

function asStringArray(value: string | string[] | undefined): string[] {
    if (value === undefined || value === "") {
        return [];
    }
    return Array.isArray(value) ? value : [value];
}

function asCurrencyArray(value: string | string[] | undefined): CurrencyCode[] {
    const raw = asStringArray(value);
    return raw.filter(
        (c): c is CurrencyCode => CurrencyCodeSchema.safeParse(c).success,
    );
}

function asPaymentMethodArray(value: string | string[] | undefined): PaymentMethod[] {
    const raw = asStringArray(value);
    return raw.filter(
        (method): method is PaymentMethod =>
            PaymentMethodSchema.safeParse(method).success,
    );
}

function asPaymentStatusArray(value: string | string[] | undefined): PaymentStatus[] {
    const raw = asStringArray(value);
    return raw.filter(
        (status): status is PaymentStatus =>
            PaymentStatusSchema.safeParse(status).success,
    );
}
